/**
 * Related product card: sync option selects to a variant id.
 * Options ship pre-selected to the first available variant; all dropdowns stay visible.
 * Every real option stays selectable, including out-of-stock values, so imagery can update.
 * ATC follows the matched variant: in-stock stays addable, out-of-stock disables the button.
 */

function markTouchEnvironment() {
  document.documentElement.classList.add("has-touch");
}

if (
  "ontouchstart" in window ||
  (navigator.maxTouchPoints != null && navigator.maxTouchPoints > 0)
) {
  markTouchEnvironment();
}

if (!window.__gatTouchEnvironmentBound) {
  window.__gatTouchEnvironmentBound = true;
  window.addEventListener("touchstart", markTouchEnvironment, {
    once: true,
    passive: true,
    capture: true
  });
}

class PdpRelatedCard extends HTMLElement {
  #form;
  #idInput;
  #optionSelects;
  #optionSteps;
  #optionSwatches;
  #variants;
  #priceEl;
  #submitButton;
  #imageEl;
  #mediaLink;
  #titleLink;
  #progressive = false;
  #syncing = false;
  #mediaSwapToken = 0;
  #closeTimer = null;

  connectedCallback() {
    this.#form = this.querySelector("form");
    this.#idInput = this.querySelector("[data-related-variant-id]");
    this.#optionSteps = Array.from(
      this.querySelectorAll("[data-option-step]")
    ).sort(
      (a, b) => Number(a.dataset.optionStep) - Number(b.dataset.optionStep)
    );
    this.#optionSelects = this.#optionSteps.map((step) =>
      step.querySelector("[data-option-position]")
    );
    this.#optionSwatches = this.#optionSteps.map((step) =>
      Array.from(step.querySelectorAll("[data-related-swatch]"))
    );
    this.#priceEl = this.querySelector("[data-related-price]");
    this.#submitButton = this.#form?.querySelector('button[type="submit"]');
    this.#imageEl = this.querySelector(".pdp-related-card__image");
    this.#mediaLink = this.querySelector(".pdp-related-card__media");
    this.#titleLink = this.querySelector(".pdp-related-card__title-link");
    this.#progressive = this.dataset.progressiveOptions === "true";

    const variantsNode = this.querySelector("[data-related-variants]");
    try {
      // Attribute first — node content is rewritten by price-formatting apps.
      // Text fallbacks keep older cached markup working.
      const raw = variantsNode
        ? variantsNode.getAttribute("data-related-variants") ||
          (variantsNode.tagName === "TEMPLATE" && variantsNode.content
            ? variantsNode.content.textContent
            : variantsNode.textContent)
        : "";
      const parsed = raw ? JSON.parse(raw) : [];
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      this.#variants = [];
    }

    this.#optionSelects.forEach((select, index) => {
      select?.addEventListener("change", () => this.#onOptionChange(index));
      // iOS native picker often commits on dismiss (blur), not change.
      select?.addEventListener("blur", () => this.#onOptionChange(index));
      select?.addEventListener("input", () => this.#onOptionChange(index));
    });

    this.#optionSwatches.forEach((swatches, index) => {
      swatches.forEach((input) => {
        input.addEventListener("click", (event) => {
          event.stopPropagation();
        });
        input.addEventListener("change", () => {
          if (!input.checked) {
            return;
          }
          this.selectOptionValue(index, input.value);
        });
      });

      /**
       * iOS Safari suppresses the radio's change event when script writes to
       * `checked` between touchend and the label's click. Commit the tapped
       * label's own value after the native activation instead of reading the
       * live :checked state, which is still stale at touchend.
       */
      this.#optionSteps[index]
        ?.querySelectorAll(".pdp-related-card__swatch-label")
        .forEach((label) => {
          const commit = () => {
            const value = this.#swatchValueForLabel(label);
            if (value) {
              window.setTimeout(() => this.selectOptionValue(index, value), 0);
            }
          };

          label.addEventListener("click", commit);
          label.addEventListener("touchend", commit, { passive: true });
        });
    });

    this.#form?.addEventListener(
      "submit",
      (event) => {
        const variant = this.#variantById(this.#idInput?.value);
        if (this.#isPurchasable(variant) && !this.#submitButton?.disabled) {
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    this.#ensureInitialSelection();
    this.#syncVariant();
    queueMicrotask(() => this.#syncVariant());
    this.#bindHoverRevealSupport();
  }

  #swatchValueForLabel(label) {
    const input =
      (label.htmlFor && this.querySelector(`#${CSS.escape(label.htmlFor)}`)) ||
      label.parentElement?.querySelector("[data-related-swatch]");
    return input?.value || "";
  }

  /**
   * Force one option to a specific value, independent of current :checked state.
   * Used by tap handlers where the browser has not settled the radio yet.
   */
  selectOptionValue(index, value) {
    const normalized = this.#normalize(value);
    if (!normalized) {
      return;
    }

    const select = this.#optionSelects[index];
    if (select) {
      const match = Array.from(select.options).find(
        (optionEl) => this.#normalize(optionEl.value) === normalized
      );
      if (match) {
        select.value = match.value;
      }
    }

    (this.#optionSwatches[index] || []).forEach((input) => {
      input.checked = this.#normalize(input.value) === normalized;
    });

    this.#onOptionChange(index);
  }

  /** Same as selectOptionValue, addressed by the Liquid option position. */
  selectOptionByPosition(position, value) {
    const index = this.#optionSteps.findIndex(
      (step) => Number(step.dataset.optionStep) === Number(position)
    );
    if (index >= 0) {
      this.selectOptionValue(index, value);
    }
  }

  /**
   * iOS Safari may not fire change on native selects before add-to-cart.
   * Read live control state and sync the hidden variant id immediately before submit.
   */
  prepareForCartAdd() {
    this.#optionSwatches.forEach((swatches, index) => {
      const checked = swatches.find((input) => input.checked);
      const select = this.#optionSelects[index];
      if (checked && select) {
        select.value = checked.value;
      }
    });

    this.#optionSelects.forEach((select) => {
      if (!select || select.selectedIndex < 0) {
        return;
      }
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption?.value) {
        select.value = selectedOption.value;
      }
    });

    this.#syncVariant();

    if (!this.#idInput?.value) {
      return "";
    }

    this.#idInput.disabled = false;
    return String(this.#idInput.value);
  }

  #usesHoverReveal() {
    return Boolean(this.closest(".template-index"));
  }

  #openCard() {
    window.clearTimeout(this.#closeTimer);
    this.classList.add("pdp-related-card--open");
  }

  #scheduleClose() {
    if (document.documentElement.classList.contains("has-touch")) {
      return;
    }

    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (this.matches(":hover")) {
        return;
      }
      if (this.contains(document.activeElement)) {
        return;
      }
      this.classList.remove("pdp-related-card--open");
    }, 350);
  }

  /**
   * Homepage cards hide the form until hover/focus. Native <select> on iOS/iPadOS
   * drops :focus-within when the OS picker opens, collapsing the form mid-tap.
   */
  #bindHoverRevealSupport() {
    if (!this.#usesHoverReveal()) {
      return;
    }

    const open = () => this.#openCard();

    this.addEventListener("pointerenter", open);
    this.addEventListener("focusin", open);
    this.addEventListener("pointerleave", () => this.#scheduleClose());
    this.addEventListener("focusout", () => this.#scheduleClose());
    this.addEventListener(
      "touchstart",
      () => {
        markTouchEnvironment();
        open();
      },
      { passive: true }
    );

    this.#optionSelects.forEach((select) => {
      if (!select) {
        return;
      }
      select.addEventListener("pointerdown", open);
      select.addEventListener("mousedown", open);
      select.addEventListener("touchstart", open, { passive: true });
      select.addEventListener("blur", () => this.#scheduleClose());
      select.addEventListener("change", open);
    });

    this.#form?.addEventListener("pointerdown", open);
    this.#submitButton?.addEventListener("touchstart", open, { passive: true });
  }

  #normalize(value) {
    return String(value ?? "")
      .replace(/\u00a0/g, " ")
      .trim();
  }

  #optionValue(variant, index) {
    if (!variant) {
      return "";
    }
    if (Array.isArray(variant.options) && variant.options[index] != null) {
      return this.#normalize(variant.options[index]);
    }
    return this.#normalize(variant[`option${index + 1}`]);
  }

  #onOptionChange(changedIndex) {
    if (this.#progressive) {
      this.#optionSelects.forEach((select, index) => {
        if (index > changedIndex && select) {
          select.value = "";
        }
      });
    } else {
      this.#reconcileLaterOptions(changedIndex);
    }

    this.#syncVariant({ changedIndex });
  }

  #selectedOptions() {
    return this.#optionSelects.map((select) => this.#normalize(select?.value));
  }

  #findVariant(options, { requireAvailable = false } = {}) {
    const normalized = (options || []).map((value) => this.#normalize(value));
    if (!normalized.length || normalized.some((value) => !value)) {
      return null;
    }

    return this.#variants.find((variant) => {
      if (requireAvailable && !this.#isPurchasable(variant)) {
        return false;
      }

      return normalized.every(
        (value, index) => this.#optionValue(variant, index) === value
      );
    });
  }

  #isPurchasable(variant) {
    return Boolean(variant && variant.available === true);
  }

  #variantById(id) {
    if (id == null || id === "") {
      return null;
    }
    const normalized = String(id);
    return this.#variants.find((variant) => String(variant.id) === normalized) || null;
  }

  #applyVariantToSelects(variant) {
    if (!variant) {
      return;
    }

    this.#optionSelects.forEach((select, index) => {
      if (!select) {
        return;
      }
      const value = this.#optionValue(variant, index);
      if (!value) {
        return;
      }

      const match = Array.from(select.options).find(
        (optionEl) => this.#normalize(optionEl.value) === value
      );
      if (match) {
        select.value = match.value;
      }
    });

    this.#syncSwatchInputs();
  }

  #syncSwatchInputs() {
    this.#optionSelects.forEach((select, index) => {
      const value = this.#normalize(select?.value);
      const selectedLabel = this.#optionSteps[index]?.querySelector(
        "[data-related-swatch-selected]"
      );
      if (selectedLabel && value) {
        selectedLabel.textContent = value;
      }

      (this.#optionSwatches[index] || []).forEach((input) => {
        input.checked = this.#normalize(input.value) === value;
      });
    });
  }

  #ensureInitialSelection() {
    if (this.#optionSelects.length === 0) {
      return;
    }

    const current = this.#selectedOptions();
    if (this.#findVariant(current)) {
      return;
    }

    const byId = this.#variantById(this.#idInput?.value);
    const preferred =
      (byId && this.#isPurchasable(byId) ? byId : null) ||
      this.#variants.find((variant) => this.#isPurchasable(variant)) ||
      byId ||
      this.#variants[0];

    this.#applyVariantToSelects(preferred);
  }

  #variantMatchScore(variant, options) {
    return (options || []).reduce((score, value, index) => {
      if (!value) {
        return score;
      }
      return score + (this.#optionValue(variant, index) === value ? 1 : 0);
    }, 0);
  }

  #resolveVariant(changedIndex) {
    const options = this.#selectedOptions();
    const exact = this.#findVariant(options);
    if (exact) {
      return exact;
    }

    if (changedIndex == null) {
      const byId = this.#variantById(this.#idInput?.value);
      return (
        (byId && this.#isPurchasable(byId) ? byId : null) ||
        this.#variants.find((variant) => this.#isPurchasable(variant)) ||
        byId ||
        this.#variants[0] ||
        null
      );
    }

    const changedValue = options[changedIndex];
    const candidates = changedValue
      ? this.#variants.filter(
          (variant) => this.#optionValue(variant, changedIndex) === changedValue
        )
      : this.#variants.slice();

    if (!candidates.length) {
      return null;
    }

    candidates.sort((a, b) => {
      const byScore =
        this.#variantMatchScore(b, options) - this.#variantMatchScore(a, options);
      if (byScore !== 0) {
        return byScore;
      }
      return Number(this.#isPurchasable(b)) - Number(this.#isPurchasable(a));
    });

    return candidates[0];
  }

  #variantMatchesPriors(variant, throughIndex) {
    for (let index = 0; index <= throughIndex; index += 1) {
      const select = this.#optionSelects[index];
      const selected = this.#normalize(select?.value);
      if (!selected) {
        continue;
      }
      if (this.#optionValue(variant, index) !== selected) {
        return false;
      }
    }
    return true;
  }

  #reconcileLaterOptions(changedIndex) {
    for (let index = changedIndex + 1; index < this.#optionSelects.length; index += 1) {
      const select = this.#optionSelects[index];
      if (!select) {
        continue;
      }

      const currentValue = this.#normalize(select.value);
      const stillValid =
        currentValue &&
        this.#variants.some(
          (variant) =>
            this.#optionValue(variant, index) === currentValue &&
            this.#variantMatchesPriors(variant, index - 1)
        );

      if (stillValid) {
        continue;
      }

      const preferred = this.#variants.find((variant) =>
        this.#variantMatchesPriors(variant, index - 1)
      );

      if (preferred) {
        const nextValue = this.#optionValue(preferred, index);
        const match = Array.from(select.options).find(
          (optionEl) => this.#normalize(optionEl.value) === nextValue
        );
        select.value = match ? match.value : "";
      } else {
        select.value = "";
      }
    }
  }

  #updateOptionVisibility() {
    if (!this.#progressive) {
      this.#optionSteps.forEach((step) => {
        step.hidden = false;
      });
      return;
    }

    let previousComplete = true;

    this.#optionSteps.forEach((step, index) => {
      const select = this.#optionSelects[index];
      const shouldShow = index === 0 || previousComplete;
      step.hidden = !shouldShow;

      if (!shouldShow && select) {
        select.value = "";
      }

      previousComplete = shouldShow && Boolean(this.#normalize(select?.value));
    });
  }

  #setSubmitState({ purchasable, label }) {
    if (!this.#submitButton) {
      return;
    }

    if (
      this.#submitButton.classList.contains("button--added-success") ||
      this.#submitButton.dataset.gatSuccessActive === "true"
    ) {
      return;
    }

    this.#submitButton.disabled = !purchasable;
    this.#submitButton.setAttribute("aria-disabled", purchasable ? "false" : "true");

    if (!label) {
      return;
    }

    const contentEl =
      this.#submitButton.contentElement ||
      this.#submitButton.querySelector(":scope > div");

    if (contentEl) {
      contentEl.textContent = label;
    } else {
      this.#submitButton.textContent = label;
    }
  }

  #syncVariant({ changedIndex } = {}) {
    if (this.#syncing) {
      return;
    }

    this.#syncing = true;

    try {
      this.#updateOptionVisibility();
      this.#refreshOptionAvailability();

      const hasOptionSelects = this.#optionSelects.length > 0;
      let variant = null;

      if (hasOptionSelects) {
        variant = this.#resolveVariant(changedIndex);

        if (variant) {
          this.#applyVariantToSelects(variant);
        }
      } else {
        variant =
          this.#variantById(this.#idInput?.value) ||
          this.#variants.find((item) => this.#isPurchasable(item)) ||
          this.#variants[0] ||
          (this.#idInput?.value
            ? {
                id: this.#idInput.value,
                available: this.dataset.productAvailable === "true"
              }
            : null);
      }

      if (!this.#idInput) {
        return;
      }

      if (!variant) {
        const productAvailable = this.dataset.productAvailable === "true";
        if (this.#variants.length === 0 && productAvailable && this.#idInput.value) {
          this.#idInput.disabled = false;
          this.#setSubmitState({
            purchasable: true,
            label: this.dataset.atcLabel
          });
          return;
        }

        this.#idInput.disabled = true;
        this.#setSubmitState({
          purchasable: false,
          label: this.dataset.soldOutLabel
        });
        return;
      }

      this.#idInput.value = String(variant.id);
      this.#updateMedia(variant);

      if (this.#priceEl && variant.price_label) {
        this.#priceEl.textContent = variant.price_label;
      }

      const purchasable = this.#isPurchasable(variant);
      this.#idInput.disabled = !purchasable;
      this.#setSubmitState({
        purchasable,
        label: purchasable ? this.dataset.atcLabel : this.dataset.soldOutLabel
      });
    } finally {
      this.#syncing = false;
    }
  }

  #imageFileKey(src) {
    if (!src) {
      return "";
    }

    try {
      const url = new URL(src, window.location.href);
      return url.pathname.replace(
        /_(?:pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*)(?=\.[a-zA-Z]+$)/,
        ""
      );
    } catch (error) {
      return String(src).split("?")[0];
    }
  }

  #toPreviewSrc(src) {
    if (!src) {
      return src;
    }

    try {
      const url = new URL(src, window.location.href);
      if (url.hostname.endsWith("myshopify.com") || url.hostname === "cdn.shopify.com") {
        return `${url.pathname}${url.search}`;
      }
      return url.href;
    } catch (error) {
      return src;
    }
  }

  #applyImage(image) {
    const imageEl = this.querySelector(".pdp-related-card__image");
    this.#imageEl = imageEl;
    if (!imageEl || imageEl.tagName !== "IMG" || !image?.src) {
      return;
    }

    const src = this.#toPreviewSrc(image.src);
    imageEl.src = src;
    if (image.srcset) {
      imageEl.srcset = image.srcset
        .split(",")
        .map((entry) => {
          const parts = entry.trim().split(/\s+/);
          parts[0] = this.#toPreviewSrc(parts[0]);
          return parts.join(" ");
        })
        .join(", ");
    } else {
      imageEl.removeAttribute("srcset");
    }
    if (image.alt) {
      imageEl.alt = image.alt;
    }
    if (imageEl.hasAttribute("data-src")) {
      imageEl.setAttribute("data-src", src);
    }
    if (imageEl.hasAttribute("data-srcset")) {
      imageEl.setAttribute("data-srcset", imageEl.srcset || "");
    }
    if (imageEl.hasAttribute("variantsrc")) {
      imageEl.setAttribute("variantsrc", src);
    }
  }

  #updateMedia(variant) {
    if (variant?.url) {
      if (this.#mediaLink) {
        this.#mediaLink.href = variant.url;
      }
      if (this.#titleLink) {
        this.#titleLink.href = variant.url;
      }
    }

    const image = variant?.featured_image;
    const imageEl = this.querySelector(".pdp-related-card__image");
    this.#imageEl = imageEl;
    if (!image?.src || !imageEl || imageEl.tagName !== "IMG") {
      return;
    }

    const currentKey = this.#imageFileKey(imageEl.currentSrc || imageEl.src);
    const nextKey = this.#imageFileKey(image.src);
    if (currentKey && currentKey === nextKey) {
      return;
    }

    const token = ++this.#mediaSwapToken;
    this.#applyImage(image);
    const liveImage = this.querySelector(".pdp-related-card__image") || imageEl;
    if (token !== this.#mediaSwapToken || !liveImage) {
      return;
    }

    try {
      liveImage.animate(
        { opacity: [0.35, 1] },
        { duration: 160, easing: "ease-out" }
      );
    } catch (error) {
      liveImage.style.opacity = "1";
    }
  }

  #refreshOptionAvailability() {
    this.#optionSelects.forEach((select) => {
      if (!select) {
        return;
      }

      Array.from(select.options).forEach((optionEl) => {
        const optionValue = this.#normalize(optionEl.value);
        optionEl.disabled = !optionValue;
      });
    });
  }
}

if (!window.customElements.get("pdp-related-card")) {
  window.customElements.define("pdp-related-card", PdpRelatedCard);
}
